import { lazy, Suspense, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import type { TFlowGraph } from '@hookpipe/contracts/flows/schemas/flow-graph.schema';

import { ErrorState, LoadingState } from '../../../shared/ui/states';
import { flowKeys, getNodeCatalog, getVersion, parseGraph } from '../../../entities/flow';
import type { TIssueDetail } from '../../../entities/issue';
import type { INodeDecoration } from '../../../widgets/flow-canvas';

// xyflow stays out of the Issues chunk until an issue on a flow step is opened.
const FlowCanvasLazy = lazy(async () => {
  const mod = await import('../../../widgets/flow-canvas');
  return { default: mod.FlowCanvas };
});

interface IFlowPathProps {
  issue: TIssueDetail;
  flowId: string;
  versionNumber: number | 'live';
}

// Board 19 "Where in the flow": the steps from the trigger down to this one, read-only.
export const FlowPath = ({ issue, flowId, versionNumber }: IFlowPathProps) => {
  const number = versionNumber;
  const version = useQuery({
    queryKey: flowKeys.version(flowId, number),
    queryFn: () => getVersion(flowId, number),
  });
  const catalog = useQuery({ queryKey: flowKeys.catalog, queryFn: getNodeCatalog });

  const target = issue.node?.key ?? null;
  const graph = useMemo((): TFlowGraph | null => {
    const full = parseGraph(version.data?.graph ?? null);
    if (!full) return null;
    // No path when the step is gone from the live version: the whole old version shows then.
    if (issue.flowPath.length === 0) return full;
    const path = new Set(issue.flowPath.map((node) => node.key));
    // Other branches keep their first step, faded: a cut edge would read "run finishes here".
    const beside = new Set(
      full.edges
        .filter((edge) => path.has(edge.from) && edge.from !== target && !path.has(edge.to))
        .map((edge) => edge.to),
    );
    const keys = new Set([...path, ...beside]);
    return {
      ...full,
      nodes: full.nodes.filter((node) => keys.has(node.key)),
      edges: full.edges.filter(
        (edge) => path.has(edge.from) && keys.has(edge.to) && edge.from !== target,
      ),
    };
  }, [version.data, issue.flowPath, target]);

  const aside = useMemo(
    () =>
      new Set(
        (graph?.nodes ?? [])
          .map((node) => node.key)
          .filter((key) => issue.flowPath.length > 0 && !issue.flowPath.some((n) => n.key === key)),
      ),
    [graph, issue.flowPath],
  );

  // The whole old version shows when the step left the live one; the failed step is what to frame.
  const fitKeys = useMemo(
    (): readonly string[] | undefined =>
      issue.flowPath.length > 0
        ? (graph?.nodes ?? []).map((node) => node.key)
        : target && graph?.nodes.some((node) => node.key === target)
          ? [target]
          : undefined,
    [issue.flowPath, target, graph],
  );

  const decorations = useMemo((): Record<string, INodeDecoration> => {
    const result: Record<string, INodeDecoration> = {};
    for (const key of aside) result[key] = { state: 'faded', subtitle: 'another branch' };
    if (target) result[target] = { state: 'failed' };
    return result;
  }, [target, aside]);

  return (
    <section className="overflow-hidden rounded-panel border border-border-sunken">
      <header className="flex h-secthead items-center gap-inset border-b border-border-sunken px-3">
        <h3 className="text-data font-semibold text-primary">Where in the flow</h3>
        <Link
          to="/flows/$flowId"
          params={{ flowId }}
          search={target ? { node: target } : {}}
          className="ml-auto text-meta text-primary underline"
        >
          Open on canvas
        </Link>
      </header>
      <div className="h-body">
        {(version.isError || catalog.isError) && (
          <ErrorState
            error={version.error ?? catalog.error}
            onRetry={() => {
              void version.refetch();
              void catalog.refetch();
            }}
          />
        )}
        {(version.isPending || catalog.isPending) && <LoadingState rows={3} />}
        {graph && catalog.data && (
          <Suspense fallback={<LoadingState rows={3} className="h-full" />}>
            <FlowCanvasLazy
              graph={graph}
              descriptors={catalog.data.nodeTypes}
              decorations={decorations}
              readOnly
              showZoomControls={false}
              showMinimap={false}
              fitKeys={fitKeys}
            />
          </Suspense>
        )}
      </div>
    </section>
  );
};
